import classNames from 'classnames'
import React, { useCallback, useEffect, useState } from 'react'
import { DirectoryColumn, DirectoryColumnDefinition, DirectoryRow, SortFn } from '.'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { Breakpoints } from '../../utilities/breakpoints'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { AccordionButton } from '../Accordion/AccordionButton'
import { Collapse } from '../Collapse'
import { LargeLink } from '../LargeLink'
import { DesktopColumnHeader } from './DesktopColumnHeader'
import { DesktopListItem } from './DesktopListItem'
import { TabletListItem } from './TabletListItem'

export interface DirectoryClassNames {
  cellClassName?: string
  headerCellClassName?: string
  headerRowClassName?: string
  listClassName?: string
  rowClassName?: string
}

export interface DirectoryProps {
  additionalClassName?: string
  arrowPositionClassName?: string
  arrowSizeClassName?: string
  classNameOverrides?: DirectoryClassNames
  collapseOnMobile?: boolean
  columnDefinitions: DirectoryColumnDefinition[]
  mainColumn?: string
  renderHeader?: (colDef: DirectoryColumnDefinition) => React.ReactNode
  rows: DirectoryRow[]
  skipSortLastRow?: boolean
  sortInitially?: boolean
  useTabletStyleInSmall?: boolean
  enableSort?: boolean
}

type SortState = {
  asc: boolean
  column: string
}

export const stringSorter: SortFn<string | undefined> = (left, right, asc) => {
  if (asc) {
    if (left === undefined) {
      return -1
    } else if (right === undefined) {
      return 1
    } else {
      return left < right ? -1 : 1
    }
  } else {
    if (left === undefined) {
      return 1
    } else if (right === undefined) {
      return -1
    } else {
      return left > right ? -1 : 1
    }
  }
}

export const numberSorter: SortFn<string | undefined> = (left, right, asc) => {
  const leftNum = left !== undefined ? parseFloat(left) : undefined
  const rightNum = right !== undefined ? parseFloat(right) : undefined

  if (leftNum === rightNum) {
    return 0
  } else if (asc) {
    if (leftNum === undefined || isNaN(leftNum)) {
      return -1
    } else if (rightNum === undefined || isNaN(rightNum)) {
      return 1
    } else {
      return leftNum - rightNum
    }
  } else {
    if (leftNum === undefined || isNaN(leftNum)) {
      return 1
    } else if (rightNum === undefined || isNaN(rightNum)) {
      return -1
    } else {
      return rightNum - leftNum
    }
  }
}

export const DirectoryList = ({
  additionalClassName,
  arrowPositionClassName,
  arrowSizeClassName,
  classNameOverrides = {},
  collapseOnMobile = true,
  columnDefinitions,
  mainColumn: mainColumnName,
  renderHeader,
  rows,
  skipSortLastRow = false,
  sortInitially = true,
  useTabletStyleInSmall = false,
  enableSort = true,
}: DirectoryProps): JSX.Element | null => {
  const [aboveTablet, setAboveTablet] = useState<boolean>(true)
  useBreakpoint(useTabletStyleInSmall ? Breakpoints.md : Breakpoints.sm, setAboveTablet)
  const isTablet = !aboveTablet

  if (isNullOrWhiteSpace(mainColumnName)) {
    mainColumnName = columnDefinitions[0]?.name
  }

  const [sortedRows, setSortedRows] = useState<DirectoryRow[]>(rows)
  const [activeIndex, setActiveIndex] = useState<number>()
  const [sortState, setSortState] = useState<SortState>()

  const sort = useCallback(
    (state: SortState) => {
      // Copy the original array, as sort() mutates the array directly
      const rowsToSort = skipSortLastRow ? rows.slice(0, -1) : rows.slice()
      const columnDef = columnDefinitions.find(colDef => colDef.name === state.column)

      const getColumnValue = (columns: DirectoryColumn[]) => {
        const columnValue = columns.find(col => col.name === state.column)?.value
        return typeof columnValue === 'string' ? columnValue.toLowerCase() : columnValue
      }

      const sorted = rowsToSort.sort((left, right) => {
        const leftValue = getColumnValue(left.columns)
        const rightValue = getColumnValue(right.columns)

        if (columnDef?.sort) {
          return columnDef.sort(leftValue, rightValue, state.asc)
        } else {
          return stringSorter(leftValue, rightValue, state.asc)
        }
      })

      if (skipSortLastRow && rows?.length > 0) {
        sorted.push(rows[rows.length - 1])
      }

      setSortedRows(sorted)
      setSortState(state)
    },
    [columnDefinitions, rows, skipSortLastRow]
  )

  useEffect(() => {
    if (sortInitially) {
      sort({
        asc: true,
        column: mainColumnName ?? '',
      })
    }
  }, [mainColumnName, sort, sortInitially])

  return (
    <div className={classNames('w-full', additionalClassName)}>
      <ul
        className={classNames(classNameOverrides.listClassName, {
          'sm:border-t sm:border-neutral-light':
            classNameOverrides.listClassName === undefined,
        })}
      >
        {(aboveTablet || !collapseOnMobile) && (
          <li
            className={classNames(classNameOverrides.headerRowClassName, {
              'flex py-20 md:py-15': classNameOverrides.headerRowClassName === undefined,
            })}
          >
            {columnDefinitions.map((colDef, index) => (
              <DesktopColumnHeader
                arrowPositionClassName={arrowPositionClassName}
                arrowSizeClassName={arrowSizeClassName}
                ascending={sortState?.column === colDef.name ? sortState.asc : false}
                className={classNameOverrides.headerCellClassName}
                isCurrentSortColumn={sortState?.column === colDef.name}
                key={index}
                enableSort={enableSort}
                onSort={asc =>
                  sort({
                    asc,
                    column: colDef.name,
                  })
                }
                staticWidthClass={colDef.staticWidthClass}
              >
                {colDef.renderHeader || renderHeader?.(colDef) || colDef.title}
              </DesktopColumnHeader>
            ))}
          </li>
        )}

        {sortedRows?.map((row, rowIndex) => {
          if (isTablet && collapseOnMobile) {
            const active = activeIndex === rowIndex
            const mainColumn = row.columns.find(col => col.name === mainColumnName)

            // Tablet and below screens have their data grouped inside accordions
            return (
              <li
                className={classNames(
                  'flex flex-col border-t border-neutral-light cursor-pointer sm:cursor-default',
                  {
                    'pb-20': active,
                  }
                )}
                key={!isNullOrWhiteSpace(row.key) ? row.key : rowIndex}
                onClick={() => setActiveIndex(active ? undefined : rowIndex)}
              >
                {mainColumn && (
                  <div className="flex justify-between items-center">
                    <span
                      className={classNames(
                        active ? 'py-10 text-primary' : 'py-7 text-secondary-darker',
                        'transition-all duration-200'
                      )}
                    >
                      {/* The row name becomes a link when the accordion is open. Use stopPropagation to disable closing the
                      accordion when clicking the link. */}
                      <LargeLink
                        disabled={!active || isNullOrWhiteSpace(mainColumn.link)}
                        fontClassName="font-t-bold text-t-400"
                        onClick={e => e.stopPropagation()}
                        title={mainColumn.value}
                        url={mainColumn.link ?? ''}
                      >
                        {mainColumn.value}
                      </LargeLink>
                    </span>
                    <AccordionButton small active={rowIndex === activeIndex} />
                  </div>
                )}

                <Collapse
                  className="transition-all duration-200"
                  contentClassName="flex flex-col"
                  isOpen={rowIndex === activeIndex}
                >
                  <ul className="tb:pr-15">
                    {row.columns
                      .filter(col => col.name !== mainColumnName)
                      .map(col => {
                        const columnDefinition = columnDefinitions.find(
                          colDef => colDef.name === col.name
                        )

                        return (
                          <TabletListItem
                            heading={columnDefinition?.title ?? ''}
                            key={col.name}
                          >
                            {columnDefinition?.render?.(col.value) || col.value}
                          </TabletListItem>
                        )
                      })}
                  </ul>
                </Collapse>
              </li>
            )
          } else {
            return (
              <li
                className={classNames(classNameOverrides.rowClassName, {
                  'flex py-15 border-t border-neutral-light items-center':
                    classNameOverrides.rowClassName === undefined,
                })}
                key={row.key ?? rowIndex}
              >
                {row.columns.map(col => {
                  const columnDefinition = columnDefinitions.find(
                    colDef => colDef.name === col.name
                  )

                  return (
                    <DesktopListItem
                      className={classNameOverrides.cellClassName}
                      link={col.link}
                      key={col.name}
                      staticWidthClass={columnDefinition?.staticWidthClass}
                    >
                      {columnDefinition?.render?.(col.value) || col.value}
                    </DesktopListItem>
                  )
                })}
              </li>
            )
          }
        })}
      </ul>
    </div>
  )
}
